import { ActionTree, GetterTree, MutationTree } from 'vuex';
import { cloneDeep, get, isEqualWith, orderBy, pick, toString } from 'lodash';
import { gql } from 'graphql-tag';
import { RootState } from '~/store';
import {
  ProdigyFigureQueryResult,
  ProdigyFigureQueriesFindAllArgs,
  ProdigyPanelQueriesFindAllArgs,
  ProdigyPanelQueryResult,
  ProdigyFigure,
  ProdigyFigureMutationsUpdateArgs
} from '~/schema';

const initialState = (): {
  figures: ProdigyFigureQueryResult['results'];
  figuresChange: Array<
    Omit<ProdigyFigure, 'imageUrl'> & { imageUrl?: string | File }
  >;
  panels: ProdigyPanelQueryResult['results'];
  loading: Record<'figure' | 'panel' | 'save', boolean>;
} => {
  return {
    figures: [],
    figuresChange: [],
    panels: [],
    loading: {
      figure: false,
      panel: false,
      save: false
    }
  };
};

export const state = () => initialState();

export type ProdigyPanelFigureState = ReturnType<typeof state>;

export const getters: GetterTree<ProdigyPanelFigureState, RootState> = {
  getFigures: ({ figures }) => figures,
  getFiguresChange: ({ figuresChange }) => figuresChange,
  getPanels: ({ panels }) => panels,
  getLoading: ({ loading }) => loading,
  getAvailableToSave: ({ figures, figuresChange }) =>
    !isEqualWith(figures, figuresChange)
};

export const mutations: MutationTree<ProdigyPanelFigureState> = {
  setFigures: (state, figures) => (state.figures = figures),
  setFiguresChange: (state, { index: indexFigure, figure }) => {
    if (
      (indexFigure === null || indexFigure === undefined) &&
      Array.isArray(figure)
    ) {
      return (state.figuresChange = figure);
    }
    return (state.figuresChange = state.figuresChange.map((value, index) =>
      indexFigure === index ? { ...value, ...figure } : value
    ));
  },
  setPanels: (state, panels) => (state.panels = panels),
  setLoading: (state, loading: typeof state.loading) =>
    (state.loading = {
      ...state.loading,
      ...loading
    }),
  resetState: (state) => Object.assign(state, initialState())
};

export const actions: ActionTree<ProdigyPanelFigureState, RootState> = {
  /**
   * Fetch Figure.
   *
   * @param {*} { commit }
   * @param {*} [variables={}]
   */
  async fetchFigure(
    { commit },
    variables: ProdigyFigureQueriesFindAllArgs = {}
  ) {
    const api = window.$nuxt.$options.$api;
    const emitter = window.$nuxt.$options.$emitter;

    try {
      commit('setLoading', { figure: true });

      const { data } = await api.query({
        query: gql`
          query FindAllProdigyFigure {
            Prodigy {
              Figure {
                findAll {
                  count
                  results {
                    id
                    imageUrl
                    gender
                    width
                    height
                    pinPanels {
                      id
                      figureId
                      lat
                      long
                      position
                      panelId
                      panel {
                        id
                        code
                        name
                      }
                    }
                  }
                }
              }
            }
          }
        `,
        variables
      });
      const { results = [] } = data?.Prodigy?.Figure
        ?.findAll as ProdigyFigureQueryResult;
      const figures = orderBy(results, ['gender'], ['desc']).map((figure) => ({
        ...figure,
        pinPanels: orderBy(get(figure, 'pinPanels', []), ['position', 'asc'])
      }));

      commit('setFigures', figures);
      commit('setFiguresChange', { figure: cloneDeep(figures) });
    } catch (error) {
      emitter.emit('TOAST', error);
    } finally {
      commit('setLoading', { figure: false });
    }
  },

  /**
   * Fetch Panel.
   *
   * @param {*} { commit }
   * @param {*} [variables={}]
   */
  async fetchPanel({ commit }, variables: ProdigyPanelQueriesFindAllArgs = {}) {
    const api = window.$nuxt.$options.$api;
    const emitter = window.$nuxt.$options.$emitter;

    try {
      commit('setLoading', { panel: true });

      const { data } = await api.query({
        query: gql`
          query FindAllProdigyFigure(
            $where: ProdigyPanelQueryFilter
            $sort: ProdigyPanelQuerySortBy
            $offset: Int
            $limit: Int
          ) {
            Prodigy {
              Panel {
                findAll(
                  where: $where
                  sort: $sort
                  offset: $offset
                  limit: $limit
                ) {
                  count
                  results {
                    id
                    code
                    name
                  }
                }
              }
            }
          }
        `,
        variables
      });
      const { results = [] } = data?.Prodigy?.Panel
        ?.findAll as ProdigyPanelQueryResult;

      commit('setPanels', results);
    } catch (error) {
      emitter.emit('TOAST', error);
    } finally {
      commit('setLoading', { panel: false });
    }
  },

  /**
   * Submit Update Figure.
   *
   * @param {*} { commit, state, dispatch }
   */
  async update({ commit, state, dispatch }) {
    const api = window.$nuxt.$options.$api;
    const emitter = window.$nuxt.$options.$emitter;

    try {
      commit('setLoading', { save: true });

      for (let index = 0; index < state.figuresChange.length; index++) {
        const figure = state.figuresChange[index];

        const variables = {
          id: figure.id,
          form: {
            ...pick(figure, ['gender', 'width', 'height'])
          }
        } as ProdigyFigureMutationsUpdateArgs;

        if (figure?.imageUrl && figure.imageUrl instanceof File) {
          variables.form.imageUrl = figure.imageUrl;
        }

        if (figure?.pinPanels && figure.pinPanels.length > 0) {
          variables.form.pinPanels = figure.pinPanels.map((pin) => {
            const _pin = {
              figureId: figure.id,
              panelId: get(pin, 'panel.id'),
              lat: toString(get(pin, 'lat')),
              long: toString(get(pin, 'long')),
              position: get(pin, 'position')
            } as any;
            if (pin?.id) _pin.id = pin.id;
            return _pin;
          });
        }

        await api.mutate({
          mutation: gql`
            mutation UpdateFigure($id: ID!, $form: ProdigyFigureForm) {
              Prodigy {
                Figure {
                  update(id: $id, form: $form) {
                    id
                  }
                }
              }
            }
          `,
          variables
        });

        commit('setLoading', { save: false });
        dispatch('fetchFigure');
      }
    } catch (error) {
      commit('setLoading', { save: false });
      emitter.emit('TOAST', error);
    }
  },

  /**
   * Cancel Update Figure.
   *
   * @param {*} { commit }
   */
  cancel({ commit }) {
    commit('resetState');
  }
};
